import React, { createContext, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { applyTheme } from '../utils/applyTheme';
import { setTheme } from '../slice/themeSlice';
import logger from '../Helpers/logger';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const dispatch = useDispatch();
    const theme = useSelector(state => state.themeData?.theme || 'light');

    useEffect(() => {
        applyTheme(theme);
        document.body.classList.add('theme-transitioning');
        const t = setTimeout(() => {
            document.body.classList.remove('theme-transitioning');
        }, 300);
        return () => clearTimeout(t);
    }, [theme]);

    // System theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleSystemThemeChange = e => {
            try {
                const storedTheme = localStorage.getItem('theme');
                if (!storedTheme) {
                    const newTheme = e.matches ? 'dark' : 'light';
                    dispatch(setTheme(newTheme));
                }
            } catch (error) {
                logger.warn('Failed to access localStorage:', error);
                const newTheme = e.matches ? 'dark' : 'light';
                dispatch(setTheme(newTheme));
            }
        };

        // Check if media query is supported
        if (mediaQuery && mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleSystemThemeChange);
            return () => {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
            };
        } else if (mediaQuery && mediaQuery.addListener) {
            // Fallback
            mediaQuery.addListener(handleSystemThemeChange);
            return () => {
                mediaQuery.removeListener(handleSystemThemeChange);
            };
        }
    }, [dispatch]);

    const contextValue = useMemo(() => ({ theme }), [theme]);

    return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useTheme = () => useContext(ThemeContext);
